<template>
  <div class="overview-by-asset">
    <s-loader
      v-if="loading"
      class="loading p-5"
      size="md"
    />
    <div
      v-else
      class="overview-by-asset__chart-wrapper"
    >
      <s-select
        class="overview-by-asset__chart-options"
        value-field="id"
        text-field="name"
        has-default-value
        v-model="chartType"
        :options="chartOptions"
        @change="updateChart"
      />
      <info-badge
        class="overview-by-asset__density-tooltip"
        boundary="window"
        placement="top"
        translation-key="dashboard.density-tooltip"
      />
      <info-badge
        class="overview-by-asset__size-tooltip"
        boundary="window"
        placement="top"
        translation-key="dashboard.size-tooltip"
      />
      <e-chart
        autoresize
        ref="scatterChart"
        :class="getChartClass"
        @click="onSelectHandler"
        @mouseover="onMouseoverHandler"
        @mouseout="onMouseoutHandler"
      />
    </div>
  </div>
</template>

<script>
import { ClassificationType } from 'sigma-mappings';
import themeService from '@/services/themeService';
import { formatter as dateTimeFormatter } from '@/services/dateTime/datetimeService';
import { formats } from '@/constants/localizer';
import { mapGetters } from 'vuex';
import dashboardService from '@/domain/services/generalPanel/dashboard';
import MathHelper from '@/services/mathHelper';
import InfoBadge from '@/components/icon/InfoBadge';

const BUBBLE_SIZE = {
  MIN: 10,
  MAX: 80,
};

const CHART_TYPES = {
  SINGLE: 1,
  CONSOLIDATED: 2,
};

export default {
  name: 'OverviewByAssetChart',
  components: {
    InfoBadge,
  },
  props: {
    filters: {
      type: Object,
      default: () => ({}),
      required: true,
    },
  },
  data: vm => ({
    alarmsOverviewByAsset: [],
    chartType: CHART_TYPES.SINGLE,
    loading: true,
    chartOptions: [
      {
        id: CHART_TYPES.SINGLE,
        name: vm.$t('dashboard.classification-view'),
      },
      {
        id: CHART_TYPES.CONSOLIDATED,
        name: vm.$t('dashboard.consolidated-view'),
      },
    ],
    themeService,
  }),
  computed: {
    ...mapGetters([
      'currentFontSize',
    ]),
    currentItemGap() {
      const gapBigFont = 110;
      const gapMediumFont = 90;
      const gapSmallFont = 70;

      if (this.currentFontSize.includes('18')) return gapBigFont;
      if (this.currentFontSize.includes('16')) return gapMediumFont;
      return gapSmallFont;
    },
    chartsData() {
      return (this.alarmsOverviewByAsset || []).reduce((previousItem, currentItem) => {
        const item = {};
        const MIN_SIZE = 0;
        const { maxSize } = this.maxDensityAndSize;
        item[currentItem.alarmClassification] = (currentItem.values || []).map(value => ({
          name: value.assetId,
          symbolSize: MathHelper.scale(value.notRecognizedAlarmsAmount,
            MIN_SIZE, maxSize, BUBBLE_SIZE.MIN, BUBBLE_SIZE.MAX),
          value: [
            value.longerTimeActedAlarms,
            value.totalAlarms,
            value.notRecognizedAlarmsAmount,
            value.longerTimeNotRecognizedAlarms,
            this.formatDateTime(value.dateTimeNotRecognizedOldestAlarms),
            value.assetName,
            value.installationName,
            value.companyName,
          ],
        }));
        return {
          ...previousItem,
          ...item,
        };
      }, {});
    },
    maxDensityAndSize() {
      const allValues = (this.alarmsOverviewByAsset || [])
        .reduce((previousItem, currentItem) => ([
          ...previousItem,
          ...currentItem.values,
        ]), []);
      return {
        maxDensity: Math.max(...allValues.map(item => item.longerTimeNotRecognizedAlarms), 0),
        maxSize: Math.max(...allValues.map(item => item.totalAlarms), 0),
      };
    },
    isSeparatedGraphs() {
      return this.chartType === CHART_TYPES.SINGLE;
    },
    getChartClass() {
      return this.isSeparatedGraphs ? 'overview-by-asset__chart' : 'overview-by-asset__chart-consolidated';
    },
  },
  mounted() {
    window.addEventListener('resize', this.onResizeHandler);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onResizeHandler);
  },
  methods: {
    formatDateTime(dateTime) {
      if (!dateTime) {
        return '-';
      }

      return dateTimeFormatter(dateTime, formats.DATETIME_SHORT_WITH_SECONDS);
    },
    getAlarmsOverviewByAsset() {
      this.loading = true;
      return dashboardService.getAlarmsOverviewByAsset(this.filters)
        .then((data) => {
          this.alarmsOverviewByAsset = data;
        })
        .finally(() => {
          this.loading = false;
          this.setChartOptions();
        });
    },
    getCurrentDesktopGrid() {
      const gridDesktopDefaults = {
        top: '70px',
        width: '25.333333333%',
        bottom: '80px',
      };

      if (this.isSeparatedGraphs) {
        return {
          grid: [
            {
              left: '6%',
              ...gridDesktopDefaults,
            },
            {
              right: '36.333333333%',
              ...gridDesktopDefaults,
            },
            {
              right: '4%',
              ...gridDesktopDefaults,
            },
          ],
          visualMapDensity: {
            right: '300px',
          },
          visualMapSize: {
            right: '3%',
          },
          legend: {
            left: '2%',
          },
        };
      }
      return {
        grid: {
          left: '6%',
          right: '4%',
          ...gridDesktopDefaults,
          width: '90%',
        },
        visualMapDensity: {
          right: '300px',
        },
        visualMapSize: {
          right: '3%',
        },
        legend: {
          left: '2%',
        },
      };
    },
    getCurrentMobileGrid() {
      if (this.isSeparatedGraphs) {
        return {
          grid: [
            {
              left: '60px',
              width: '75%',
              top: '120px',
              height: '230px',
            },
            {
              left: '60px',
              width: '75%',
              top: '430px',
              height: '230px',
            },
            {
              left: '60px',
              width: '75%',
              top: '740px',
              height: '230px',
            },
          ],
          visualMapDensity: {
            left: 'center',
            itemHeight: 220,
          },
          visualMapSize: {
            left: 'center',
            top: '50px',
            itemHeight: 220,
          },
          legend: {
            left: 'center',
          },
        };
      }

      return {
        grid: {
          left: '60px',
          width: '75%',
          top: '120px',
          height: '230px',
        },
        visualMapDensity: {
          left: 'center',
          itemHeight: 220,
        },
        visualMapSize: {
          left: 'center',
          top: '50px',
          itemHeight: 220,
        },
        legend: {
          left: 'center',
        },
      };
    },
    getMediaQueryOptions() {
      const mobile = this.getCurrentMobileGrid();
      const desktop = this.getCurrentDesktopGrid();

      const screenSize = themeService.getCurrentScreenSize();
      switch (screenSize) {
        case 'xs':
        case 'sm':
        case 'md':
          return mobile;
        default:
          return desktop;
      }
    },
    tooltipFormatter(param) {
      const { value } = param;
      return `
        <div>${value[7]}</div>
        <div>${value[6]}</div>
        <div>${value[5]}</div>
        <div>${this.$t('dashboard.asset.alarms-total')}：${value[1]}</div>
        <div>${this.$t('dashboard.asset.longer-time-alarms-acted')}：${value[0]}</div>
        <div>${this.$t('dashboard.asset.total-alarms-not-recognized')}：${value[2]}</div>
        <div>${this.$t('dashboard.asset.longer-time-alarms-not-recognized')}：${value[3]}</div>
        <div>${this.$t('dashboard.asset.oldest-not-recognized')}：${value[4]}</div>
      `;
    },
    getXAxis(gridIndex) {
      return {
        gridIndex,
        type: 'value',
        name: this.$t('dashboard.asset.longer-time-by-asset').toUpperCase(),
        nameLocation: 'center',
        nameGap: 25,
        nameTextStyle: {
          fontSize: '0.85rem',
          fontWeight: 'bold',
        },
        boundaryGap: ['0', '5%'],
        axisLine: {
          show: true,
          lineStyle: {
            width: 2,
            color: themeService.getStyleVar('color-dash-border'),
          },
        },
        axisTick: {
          show: false,
        },
        splitLine: {
          show: true,
          lineStyle: {
            type: 'dashed',
            color: themeService.getStyleVar('color-grid-x-axis'),
          },
        },
        axisLabel: {
          textStyle: {
            fontWeight: 'bold',
          },
        },
      };
    },
    getYAxis(gridIndex) {
      return {
        gridIndex,
        type: 'value',
        name: this.$t('dashboard.asset.total-alarms-by-asset').toUpperCase(),
        nameLocation: 'center',
        nameGap: 35,
        nameRotate: 90,
        nameTextStyle: {
          fontSize: '0.85rem',
          fontWeight: 'bold',
        },
        boundaryGap: ['0', '5%'],
        axisLine: {
          show: true,
          lineStyle: {
            width: 2,
            color: themeService.getStyleVar('color-dash-border'),
          },
        },
        axisTick: {
          show: false,
        },
        splitLine: {
          show: true,
          lineStyle: {
            type: 'dashed',
            color: themeService.getStyleVar('color-grid-y-axis'),
          },
        },
        axisLabel: {
          textStyle: {
            fontWeight: 'bold',
          },
        },
      };
    },
    getCurrentXAsis() {
      if (this.isSeparatedGraphs) {
        return [
          this.getXAxis(0),
          this.getXAxis(1),
          this.getXAxis(2),
        ];
      }
      return this.getXAxis(0);
    },
    getCurrentYAsis() {
      if (this.isSeparatedGraphs) {
        return [
          this.getYAxis(0),
          this.getYAxis(1),
          this.getYAxis(2),
        ];
      }

      return this.getYAxis(0);
    },
    getCurrentAxis(value) {
      if (this.isSeparatedGraphs) {
        return value;
      }

      return 0;
    },
    getOptions() {
      const red = themeService.getStyleVar('color-critical');
      const yellow = themeService.getStyleVar('color-urgent');
      const blue = themeService.getStyleVar('color-ordinary');
      const isSafari = themeService.isSafari();

      const safariOptions = !isSafari ? {} : {
        legends: {
          orient: 'horizontal',
          itemGap: 40,
          left: 0,
          itemWidth: 50,
        },
        textStyle: {
          fontSize: '14px',
        },
        itemStyle: {
          shadowBlur: 0,
        },
      };
      const itemStyle = {
        opacity: 0.8,
        shadowBlur: 4,
        shadowOffsetX: 0,
        shadowOffsetY: 3,
        shadowColor: themeService.getStyleVar('color-black-38'),
        ...safariOptions.itemStyle,
      };
      const visualMapDefault = {
        inverse: true,
        orient: 'horizontal',
        top: 'top',
        itemWidth: 20,
        itemHeight: 120,
        calculable: true,
        precision: 0.1,
        textGap: 35,
        textStyle: {
          fontSize: BUBBLE_SIZE.min,
        },
      };

      return {
        color: [red, yellow, blue],
        grid: this.getMediaQueryOptions().grid,
        tooltip: {
          trigger: 'item',
          confine: true,
          textStyle: {
            color: 'white',
          },
          backgroundColor: themeService.getStyleVar('color-background-tooltip'),
          formatter: this.tooltipFormatter,
        },
        xAxis: this.getCurrentXAsis(),
        yAxis: this.getCurrentYAsis(),
        axisPointer: {
          show: true,
          triggerTooltip: false,
        },
        visualMap: [
          {
            ...visualMapDefault,
            ...this.getMediaQueryOptions().visualMapSize,
            min: 0,
            max: this.maxDensityAndSize.maxSize,
            formatter: value => (`\n${Math.round(value)}`),
            dimension: 2,
            text: [this.$t('dashboard.asset.size').toUpperCase()],
            textStyle: {
              color: themeService.getFontColor(),
            },
            inRange: {
              symbolSize: [BUBBLE_SIZE.MIN, BUBBLE_SIZE.MAX],
            },
            outOfRange: {
              symbolSize: [BUBBLE_SIZE.MIN, BUBBLE_SIZE.MAX],
              color: [themeService.getStyleVar('color-white-40')],
            },
            controller: {
              inRange: {
                color: [themeService.getStyleVar('color-visual-map-size')],
              },
              outOfRange: {
                color: [themeService.getStyleVar('color-neutral-light')],
              },
            },
          },
          {
            ...visualMapDefault,
            min: 0,
            max: this.maxDensityAndSize.maxDensity,
            ...this.getMediaQueryOptions().visualMapDensity,
            dimension: 3,
            text: [this.$t('dashboard.asset.density').toUpperCase()],
            textStyle: {
              color: themeService.getFontColor(),
            },
            inRange: {
              colorLightness: [0.9, 0.5],
            },
            outOfRange: {
              color: [themeService.getStyleVar('color-white-40')],
            },
            controller: {
              inRange: {
                colorLightness: this.getColorLightness(),
                color: [themeService.getStyleVar('color-black-100-no-opacity')],
              },
              outOfRange: {
                color: [themeService.getStyleVar('color-neutral-light')],
              },
            },
          },
        ],
        legend: {
          ...this.getMediaQueryOptions().legend,
          bottom: 'bottom',
          data: [
            this.$t('dashboard.asset.alarm-critical').toUpperCase(),
            this.$t('dashboard.asset.alarm-urgent').toUpperCase(),
            this.$t('dashboard.asset.alarm-ordinary').toUpperCase(),
          ],
          itemGap: this.currentItemGap,
          itemWidth: 8,
          itemHeight: 8,
          textStyle: {
            fontSize: '1em',
            color: [red, yellow, blue],
            fontWeight: 'bold',
            ...safariOptions.textStyle,
          },
          ...safariOptions.legends,
        },
        series: [
          {
            name: this.$t('dashboard.asset.alarm-critical').toUpperCase(),
            type: 'scatter',
            xAxisIndex: this.getCurrentAxis(0),
            yAxisIndex: this.getCurrentAxis(0),
            itemStyle,
            data: this.chartsData[ClassificationType.CRITICAL],
            emphasis: {
              itemStyle: {
                borderColor: themeService.getStyleVar('color-critical-border'),
                borderWidth: 3,
              },
            },
          },
          {
            name: this.$t('dashboard.asset.alarm-urgent').toUpperCase(),
            type: 'scatter',
            xAxisIndex: this.getCurrentAxis(1),
            yAxisIndex: this.getCurrentAxis(1),
            itemStyle,
            data: this.chartsData[ClassificationType.URGENT],
            emphasis: {
              itemStyle: {
                borderColor: themeService.getStyleVar('color-urgent-border'),
                borderWidth: 3,
              },
            },
          },
          {
            name: this.$t('dashboard.asset.alarm-ordinary').toUpperCase(),
            type: 'scatter',
            xAxisIndex: this.getCurrentAxis(2),
            yAxisIndex: this.getCurrentAxis(2),
            itemStyle,
            data: this.chartsData[ClassificationType.ORDINARY],
            emphasis: {
              itemStyle: {
                borderColor: themeService.getStyleVar('color-ordinary-border'),
                borderWidth: 3,
              },
            },
          },
        ],
      };
    },
    getColorLightness() {
      if (themeService.isDarkTheme()) {
        return [0.5, 0.9];
      }

      return [0.9, 0.5];
    },
    onResizeHandler() {
      this.setChartOptions();
    },
    setChartOptions() {
      this.$nextTick(() => {
        this.$refs.scatterChart.refresh();
        this.$refs.scatterChart.delegateMethod(
          'setOption',
          this.getOptions(),
          {
            replaceMerge: [
            'title',
             'grid',
             'legend',
             'tooltip',
             'series',
            ],
          },
        );
      });
    },
    onSelectHandler(params) {
      const classification = this.getClassificationBySeriesIndex(params.seriesIndex);
      const selected = {
        assetsIds: [params.data.name],
        classifications: [classification],
      };
      this.$emit('onBubbleClick', selected);
    },
    getClassificationBySeriesIndex(index) {
      switch (index) {
        case 0:
          return ClassificationType.CRITICAL;
        case 1:
          return ClassificationType.URGENT;
        case 2:
          return ClassificationType.ORDINARY;
        default:
          return null;
      }
    },
    onMouseoverHandler(data) {
      this.$refs.scatterChart.dispatchAction({
        type: 'highlight',
        seriesIndex: [0, 1, 2],
        name: data.name,
      });
    },
    onMouseoutHandler(data) {
      this.$refs.scatterChart.dispatchAction({
        type: 'downplay',
        seriesIndex: [0, 1, 2],
        name: data.name,
      });
    },
    updateChart() {
      this.setChartOptions();
    },
  },
};
</script>

<style lang="sass" scoped>
@import "src/styles/defaults/mobile-first"
.loading
  min-height: 380px


.overview-by-asset
  &__size-tooltip
    position: absolute
    right: calc(140px + 3%)
    top: 1.25%
    z-index: 100
    +media-max-md()
      display: none
  &__density-tooltip
    position: absolute
    right: calc(440px)
    top: 1.25%
    z-index: 100
    +media-max-md()
      display: none
  &__chart
    width: 100%
    height: 380px
    +media-max-md()
      height: calc((350px * 3) + 60px)
      padding-top: 45px
    &-consolidated
      width: 100%
      height: 380px
      +media-max-md()
        height: 500px
        padding-top: 45px
    &-wrapper
      position: relative
  &__chart-options
    position: absolute !important
    z-index: 1
    width: auto
    left: 2%
    +media-max-md()
      width: 100%
      left: 0
    & ::v-deep
      .dropdown-toggle
        background-color: transparent
        border: none
      .select__dropdown-content-wrapper, .select__dropdown-content-slot-wrapper
        text-overflow: unset
        width: 100%

@media not all and (min-resolution:.001dpcm)
  @supports (-webkit-appearance:none) and (stroke-color:transparent)
    +media-max-md()
      .overview-by-asset
        &__chart
          height: calc((350px * 3) + 100px)
          &-consolidated
            height: 520px
</style>
